import { useQuery } from '@apollo/client';
import { loadCardsQuery } from './LoadCards.query.wp';
import { transformLoadCards } from './LoadCards.transform.wp';

// Cards per page for the paginated CardGrid (build-time page 1 + client-side pages).
export const CARD_GRID_PAGE_SIZE = 30;

// Builds the GraphQL variables for fetching cards from a post type.
export function buildCardVariables(block = {}) {
  // console.log(block);

  const {
    filter,
    sourcePostType = 'news',
    search,
    taxonomy,
    taxonomySecondary,
    selectedPosts,
    selectedTaxonomies,
    numPosts = 9,
    paged,
    orderby,
  } = block;

  const variables = { postType: sourcePostType, numPosts };

  if (orderby) variables.orderby = orderby;

  // WP defaults paged to 1, so only send it past the first page.
  if (paged && paged > 1) variables.paged = paged;

  if (search) variables.search = search;

  // Active taxonomy filter selection from the card-grid filter UI:
  // { [taxonomyName]: string[] /* term slugs */ }. Multiple terms within a
  // taxonomy => OR; multiple taxonomies => AND (taxRelation).
  const taxonomyFilters = Object.entries(selectedTaxonomies || {})
    .filter(([, terms]) => Array.isArray(terms) && terms.length > 0)
    .map(([tax, terms]) => ({ taxonomy: tax, terms }));

  if (taxonomyFilters.length) {
    variables.taxonomies = taxonomyFilters;
    variables.taxRelation = 'AND';
  }

  if (filter === 'taxonomy') {
    if (taxonomy) variables.primaryTaxonomy = taxonomy;
    if (taxonomySecondary) variables.secondaryTaxonomy = taxonomySecondary;
  } else if (
    filter === 'select' &&
    Array.isArray(selectedPosts) &&
    selectedPosts.length
  ) {
    variables.ids = selectedPosts;
  }

  // console.log(variables);

  return variables;
}

/**
 * Resolves the cards to display for a card block.
 *
 * Cards are normally pre-fetched in getStaticProps and arrive on `block.cards`.
 * When they weren't (dynamic scenarios, or a failed static fetch), this falls
 * back to fetching client-side via Apollo. The query is skipped otherwise.
 *
 * @param {Object} block - The transformed block data.
 * @returns {{ displayCards: Array, loading: boolean, error: ApolloError|undefined }}
 */
export function useLoadCards(block = {}) {
  // console.log(block);
  const { source, cards = [] } = block;
  const hasCards = Array.isArray(cards) && cards.length > 0;

  // Only fetch client-side if cards weren't pre-fetched in getStaticProps.
  const shouldFetchClientSide = source === 'postType' && !hasCards;

  const { data, loading, error } = useQuery(loadCardsQuery, {
    variables: buildCardVariables(block),
    skip: !shouldFetchClientSide,
  });

  const displayCards = hasCards
    ? cards
    : transformLoadCards(data?.cardPosts?.posts || []);

  return {
    displayCards,
    loading: shouldFetchClientSide && loading,
    error,
  };
}
